---
title: "Сколько полных недель между датами и куда деваются лишние дни"
description: "Как перевести разницу между датами в полные недели и остаток дней, не перепутать 2,43 недели с 2 неделями и выбрать включение конечной даты."
locale: "ru"
date: "2026-08-03"
tags: ["недели между датами", "разница дат", "календарные дни"]
relatedTools: ["date-difference"]
translations:
  en: "how-many-full-weeks-between-two-dates"
faq:
  - q: "Как посчитать полные недели между двумя датами?"
    a: "Сначала найдите число календарных дней, затем разделите его на 7. Целая часть даст полные недели, остаток от деления даст лишние дни."
  - q: "Входит ли конечная дата в расчёт?"
    a: "По умолчанию dateify считает расстояние между границами и не добавляет конечный день. Опция включения конечной даты прибавляет один день ко всему результату."
  - q: "Полная неделя обязана идти с понедельника по воскресенье?"
    a: "Нет. В калькуляторе это любой непрерывный блок из 7 календарных дней. Для номера календарной недели нужен отдельный инструмент ISO week."
---

Спринт начался в понедельник 3 августа 2026 года, релиз назначен на четверг
20 августа. В отчёте нужны не «17 дней» и не «третья неделя месяца», а обычная
длительность: 2 полные недели и 3 дня.

Откройте [калькулятор разницы между датами](/raznica-mezhdu-datami), укажите
3 и 20 августа. По умолчанию он покажет 17 календарных дней, `2,43` недели и
отдельно `2 нед. 3 дн.`. Последняя запись обычно удобнее для плана работ:
остаток видно сразу, без десятичной дроби.

Арифметика здесь короткая:

```text
17 / 7 = 2 полные недели, остаток 3 дня
```

`2,43 недели` и `2 недели 3 дня` описывают один период. Дробная часть `0,43`
не означает 43 дня и не означает, что началась третья полная неделя. Это
примерно три седьмых недели.

## Конечная дата меняет остаток

Расстояние от 3 августа до 20 августа равно 17 дням. Такой режим удобен для
дедлайна: от начала понедельника до начала четверга прошло 17 суток.

Если формулировка звучит как «с 3 по 20 августа включительно», включите опцию
конечной даты. Получится 18 дней, то есть 2 полные недели и 4 дня. Один флажок
меняет одновременно дни, десятичные недели и остаток. Нельзя прибавить единицу
только к строке с днями, оставив недели прежними.

Для одинаковых дат разница по умолчанию равна нулю. Во включительном режиме
этот же диапазон равен одному календарному дню. Такой пример хорошо проверяет,
что заказчик и разработчик договорились об одной границе.

Если даты введены в обратном порядке, dateify меняет их местами и показывает
положительную длительность. В результате есть заметка о перестановке, поэтому
ошибка ввода не маскируется.

## Полные недели не равны календарным неделям

Полная неделя в этом расчёте - любой непрерывный блок из семи дней. Это
семисуточный интервал: например, от начала четверга до начала следующего
четверга. Границы понедельник-воскресенье здесь не участвуют.

Если задача звучит как «к какой ISO-неделе относится 20 августа», откройте
[номер недели по дате](/nomer-nedeli). Это другой вопрос: инструмент возвращает
ISO-год, номер недели и диапазон понедельник-воскресенье. Число пересечённых
строк календаря не заменяет длительность между датами.

Для рабочих недель тоже нужен другой расчёт. В периоде из 17 календарных дней
есть выходные, а в России могут быть федеральные праздники и переносы. Их можно
проверить в [калькуляторе рабочих дней](/rabochie-dni-mezhdu-datami), где режим
федеральной пятидневки работает только для годов с утверждёнными данными
производственного календаря. Региональные праздники и личный график он не
угадывает.

## Здесь считаются даты, а не часы

Калькулятор принимает календарные даты без времени и часового пояса. Он отвечает
на вопрос про дни и недели, но не обещает, что между двумя полуночами всегда
прошло ровно `N × 24` часов. Для интервала с часами, минутами или переходом на
летнее время нужен расчёт с учётом времени и часового пояса, которого на этой
странице нет.

Общий сценарий с месяцами, годами и рабочими днями разобран в статье
[как посчитать разницу между датами](/blog/kak-poschitat-raznicu-mezhdu-datami).
Для недель сначала зафиксируйте одно решение: конечный день входит в период или
нет. После этого деление на 7 уже не оставляет места для спора.
